import { UzButton, UzDropdownMenu, UzInput, type UzDropdownMenuItem } from '@utazzo/ui';
import { Check, Download, FilterX, Loader2, PanelLeft, Search, X } from 'lucide-react';
import React, { useEffect, useState, type ReactNode } from 'react';

import { DataGridFilterChip } from './DataGridFilterChip';
import {
  hiddenFilterLabel,
  type DataGridColumn,
  type DataGridFilter,
  type DataGridState,
  type Density,
} from './types';

import { useCanExportData } from '@/features/auth/use-auth';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { cn } from '@/lib/cn';

const DEBOUNCE_MS = 300;

export interface DataGridToolbarProps<TRow> {
  columns: ReadonlyArray<DataGridColumn<TRow>>;
  filters: ReadonlyArray<DataGridFilter>;
  state: DataGridState;
  /**
   * Current page window, echoed as "Showing 1-20 of 265" beside the actions.
   * The footer says the same thing, but on a long list that sits below the
   * fold - so after filtering, "how many did that leave?" means scrolling to
   * the bottom to find out. Null while loading / on error.
   */
  pagination?: { page: number; pageSize: number; totalItems: number } | null;
  onSearchChange: (q: string) => void;
  onFilterChange: (id: string, value: string) => void;
  onDensityChange: (density: Density) => void;
  onHiddenChange: (hidden: string[]) => void;
  searchPlaceholder?: string;
  toolbarActions?: ReactNode;
  /** Rendered in the filter row, after the chips: a date range picker and the
   *  like, which a select chip cannot express. */
  filtersExtra?: ReactNode;
  /** When set, a toolbar "Export" button exports ALL matching rows (the page
   *  supplies the fetch-all + format logic). May be async; a spinner shows. */
  onExport?: () => void | Promise<void>;
  /** Label for the export button (e.g. "Export CSV"). Defaults to "Export". */
  exportLabel?: string;
  /** Overrides the default ADMIN-only export gate for this grid (e.g. finance
   *  listings pass `useCanExportFinanceData()` so FINANCE can export too). When
   *  omitted, export stays ADMIN-only (`useCanExportData`). */
  canExportOverride?: boolean;
}

const checkIcon = (visible: boolean): React.JSX.Element => (
  <Check
    className="h-3.5 w-3.5"
    aria-hidden
    style={{ visibility: visible ? 'visible' : 'hidden' }}
  />
);

/**
 * "Showing 20 of 265" - how many rows are on screen against how many match.
 *
 * A count, not the footer's `1-20` range: this line answers "how much am I
 * looking at", and it tracks the Rows selector, so switching to 50 reads
 * "Showing 50 of 265". The last page counts what it actually holds ("Showing
 * 5 of 265"), never the page size it was asked for.
 */
const rangeLabel = (p: { page: number; pageSize: number; totalItems: number }): string => {
  if (p.totalItems === 0) return 'No results';
  const totalPages = Math.max(Math.ceil(p.totalItems / p.pageSize), 1);
  const safePage = Math.min(Math.max(p.page, 1), totalPages);
  const start = (safePage - 1) * p.pageSize + 1;
  const end = Math.min(safePage * p.pageSize, p.totalItems);
  return `Showing ${end - start + 1} of ${p.totalItems}`;
};

export function DataGridToolbar<TRow>({
  columns,
  filters,
  state,
  pagination,
  onSearchChange,
  onFilterChange,
  onDensityChange,
  onHiddenChange,
  searchPlaceholder = 'Search',
  toolbarActions,
  filtersExtra,
  onExport,
  exportLabel = 'Export',
  canExportOverride,
}: DataGridToolbarProps<TRow>): React.JSX.Element {
  // Local draft so the input stays responsive while the URL update is debounced.
  const [draft, setDraft] = useState(state.q);
  const debounced = useDebouncedValue(draft, DEBOUNCE_MS);
  const [exporting, setExporting] = useState(false);
  // 2026-07-23. Bulk export is ADMIN-only. Gated HERE - the one place every
  // grid in the app renders its export button - rather than at each of the
  // ~40 call sites, so a page can't opt out by forgetting the check. Pages
  // keep passing `onExport` unconditionally; it's simply never wired up for
  // a non-admin, so the handler can't fire from a stale DOM node either.
  const canExportDefault = useCanExportData();
  const canExport = canExportOverride ?? canExportDefault;
  const exportEnabled = Boolean(onExport) && canExport;

  const handleExport = async (): Promise<void> => {
    if (!onExport || !canExport || exporting) return;
    setExporting(true);
    try {
      await onExport();
    } finally {
      setExporting(false);
    }
  };

  // Push the debounced value upstream when it changes. Compare against state.q
  // so external resets (back/forward nav) don't fight the local draft.
  useEffect(() => {
    if (debounced !== state.q) {
      onSearchChange(debounced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSearchChange identity is stable enough; we only fire when the debounced text or external state.q changes.
  }, [debounced, state.q]);

  // Resync draft if URL state changed from outside (back/forward navigation).
  useEffect(() => {
    setDraft(state.q);
  }, [state.q]);

  // A filter is "active" when its URL value is a non-empty string. Multi-select
  // chips store a CSV, so any non-empty string counts.
  const activeFilterCount = filters.filter((f) => (state.filters[f.id] ?? '') !== '').length;

  const clearFilters = (): void => {
    for (const f of filters) {
      if ((state.filters[f.id] ?? '') !== '') onFilterChange(f.id, '');
    }
  };

  const toggleHidden = (columnId: string, hide: boolean): void => {
    const next = hide
      ? Array.from(new Set([...state.hidden, columnId]))
      : state.hidden.filter((id) => id !== columnId);
    onHiddenChange(next);
  };

  const viewItems: UzDropdownMenuItem[] = [
    { type: 'label', label: 'Density' },
    {
      type: 'item',
      label: 'Comfortable',
      icon: checkIcon(state.density === 'comfortable'),
      onSelect: () => onDensityChange('comfortable'),
    },
    {
      type: 'item',
      label: 'Compact',
      icon: checkIcon(state.density === 'compact'),
      onSelect: () => onDensityChange('compact'),
    },
    { type: 'separator' },
    { type: 'label', label: 'Columns' },
    ...columns
      .filter((c) => !c.alwaysVisible)
      .map((c): UzDropdownMenuItem => {
        const checked = !state.hidden.includes(c.id);
        const headerLabel = typeof c.header === 'string' && c.header ? c.header : c.id;
        return {
          type: 'item',
          label: headerLabel,
          icon: checkIcon(checked),
          onSelect: () => toggleHidden(c.id, checked),
        };
      }),
  ];

  // Hidden filters get no chip - only a removable pill while one is active.
  const chipFilters = filters.filter((f) => !f.hidden);
  const activeHiddenFilters = filters.filter((f) => f.hidden && (state.filters[f.id] ?? '') !== '');
  const hasFilters = chipFilters.length > 0 || activeHiddenFilters.length > 0;

  return (
    // Two bands, because they answer different questions. Search + the row
    // count + the actions belong to the LIST; the chips describe WHAT is in it,
    // so they get their own full-width band sitting directly on the table -
    // where a catalog's eight or nine of them still read as one line instead of
    // wrapping around the search box.
    <div className="flex flex-col">
      <div
        className={cn(
          // Phones: the search box takes the first line on its own, then the
          // count and the actions share the second (the left group is
          // `contents` there, so its children lay out as siblings). From `sm`
          // it is one line: search + count on the left, actions on the right.
          'flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 sm:flex-nowrap sm:gap-3',
          !hasFilters && 'border-b border-[var(--ds-border)]',
        )}
      >
        {/* The count sits with the search box, not with the actions: it is the
            answer to what was just typed or filtered, so it reads left-to-right
            with the thing that changed it. */}
        <div className="flex min-w-0 flex-wrap items-center gap-3 max-sm:contents">
          <UzInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={searchPlaceholder}
            leftAdornment={<Search className="h-4 w-4" />}
            containerClassName="w-full sm:w-80"
            className="h-[38px]"
          />
          {pagination && (
            <span className="text-[13px] whitespace-nowrap text-[var(--fg-muted)] tabular-nums">
              {rangeLabel(pagination)}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 max-sm:ml-auto">
          {toolbarActions}
          {exportEnabled && (
            <UzButton
              variant="ghost"
              size="sm"
              className="h-[38px]"
              onClick={() => void handleExport()}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {exportLabel}
            </UzButton>
          )}
          <UzDropdownMenu
            align="end"
            trigger={
              <UzButton
                variant="ghost"
                size="icon"
                className="h-[38px] w-[38px]"
                aria-label="View options"
              >
                <PanelLeft className="h-3.5 w-3.5" />
              </UzButton>
            }
            items={viewItems}
          />
        </div>
      </div>

      {hasFilters && (
        // Padding under the chips only - the gap above them is the search
        // row's own bottom padding, and doubling up pushed the two bands apart.
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--ds-border)] px-3 pb-3.5">
          {activeHiddenFilters.map((filter) => {
            const value = state.filters[filter.id] ?? '';
            const label =
              filter.options.find((o) => o.value === value)?.label ?? hiddenFilterLabel(value);
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => onFilterChange(filter.id, '')}
                title={`Remove the ${filter.label.toLowerCase()} filter`}
                className="focus-visible:ring-ring inline-flex h-[34px] cursor-pointer items-center gap-1.5 rounded-md border border-[#0c3b4d]/25 bg-[#0c3b4d]/[0.06] px-2.5 text-[13px] text-[#0c3b4d] transition-colors hover:bg-[#0c3b4d]/10 focus-visible:ring-2 focus-visible:outline-none"
              >
                <span className="text-[var(--fg-muted)]">{filter.label}:</span>
                <span className="max-w-[16rem] truncate font-medium">{label}</span>
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            );
          })}
          {chipFilters.map((filter) => (
            <DataGridFilterChip
              key={filter.id}
              filter={filter}
              value={state.filters[filter.id] ?? ''}
              onChange={(v) => onFilterChange(filter.id, v)}
            />
          ))}
          {filtersExtra}
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              title="Clear filters"
              className="focus-visible:ring-ring inline-flex h-[34px] cursor-pointer items-center gap-1 rounded-md px-1.5 text-[13px] text-[var(--fg-muted)] transition-colors hover:text-[var(--fg)] focus-visible:ring-2 focus-visible:outline-none"
            >
              <FilterX className="h-3.5 w-3.5" aria-hidden />
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
